import { AxiosResponse } from 'axios';
import { attach, createEffect, Effect, EffectResult, restore } from 'effector';
import { request } from 'src/common/request';
import { Group } from 'src/domain/group';
import { FetchCollectionResponse, ResponseCollection } from 'src/domain/response-collection';
import { User } from 'src/domain/user';
import { user$ } from 'src/store/user.store';

const TAKE = window.innerHeight > 900 ? 12 : 9;

export type MakeGroupFavoriteParams = {
	user?: string,
	group: string,
	type: 'add' | 'remove'
}
export type FetchGroupsEffect = Effect<{ page: number }, FetchCollectionResponse<Group>>

const fetchGroupsFx = createEffect<{ url: string, page: number, params?: { [key: string]: string | undefined } }, FetchCollectionResponse<Group>>({
	handler: async (payload) => {
		const params = {
			take: TAKE,
			skip: TAKE * payload.page,
			...(payload.params || {}),
		};
		const { data: { data, meta } }: AxiosResponse<ResponseCollection<Group>> = await request.get(payload.url, { params });
		return { data, meta: { page: payload.page, total: Math.ceil(meta.total / TAKE) } };
	},
});

export const fetchOpenGroupsFx: FetchGroupsEffect = attach({
	source: { user: user$ },
	mapParams: (payload: { page: number }, source) => ({
		url: '/groups',
		page: payload.page,
		params: { user: source.user?.id },
	}),
	effect: fetchGroupsFx,
});
export const fetchUserGroupsFx: FetchGroupsEffect = attach({
	source: { user: user$ },
	mapParams: (payload: { page: number }, source: { user: User | null }) => ({
		url: `/users/${source.user?.id}/groups`,
		page: payload.page,
	}),
	effect: fetchGroupsFx,
});
export const fetchFavoriteGroupsFx: FetchGroupsEffect = attach({
	source: { user: user$ },
	mapParams: (payload: { page: number }, source: { user: User | null }) => ({
		url: `/users/${source.user?.id}/favorite_groups`,
		page: payload.page,
	}),
	effect: fetchGroupsFx,
});

export const groupFavoriteFx = attach({
	source: { user: user$ },
	mapParams: (payload: Omit<MakeGroupFavoriteParams, 'user'>, source) => ({ type: payload.type, group: payload.group, user: source.user?.id }),
	effect: createEffect<MakeGroupFavoriteParams, string | undefined>({
		handler: async (payload) => {
			const url = `/groups/${payload.group}/favorite`;
			const data = { user: payload.user };

			if (payload.type === 'add') {
				await request.post(url, data);
			} else {
				await request.delete(url, { data });
			}

			return payload.user;
		},
	}),
});

export const openGroups$ = restore(fetchOpenGroupsFx.doneData, null)
	.on(groupFavoriteFx.done, makeGroupFavoriteHandler);
export const userGroups$ = restore(fetchUserGroupsFx.doneData, null)
	.on(groupFavoriteFx.done, makeGroupFavoriteHandler);
export const favoriteGroups$ = restore(fetchFavoriteGroupsFx.doneData, null);

function makeGroupFavoriteHandler(state: FetchCollectionResponse<Group> | null, payload: { params: Omit<MakeGroupFavoriteParams, 'user'>; result: EffectResult<any> }): FetchCollectionResponse<Group> | null {
	if (!state) { return state; }

	return {
		...state,
		data: state.data.map(item => item.id !== payload.params.group ? item : ({
			...item,
			favorite: payload.params.type === 'add' ?
				{ id: payload.result as string } :
				null,
		})),
	};
}
